import { useCallback, useEffect, useRef, useState } from "react";
import SectionHead from "./SectionHead";
import useLang from "../i18n/LanguageContext";
import useMediaQuery from "../hooks/useMediaQuery";
import flikkImg from "../assets/img/flikk.jpg";
import deskwrenImg from "../assets/img/deskwren.jpg";
import tadoyImg from "../assets/img/tadoy-cafe.jpg";
import "./Work.css";

// Things in a screenshot the critter can stand on and press. In Flikk's: the top
// of the phone and the three swipe labels. left/width/top are % of the image
// as it is shown (16:10, cropped from the sides).
const FLIKK_SPOTS = [
  { id: "phone", left: 57.4, width: 18.9, top: 20.9 },
  { id: "keep", left: 73.2, width: 5.1, top: 36.4 },
  { id: "toss", left: 55.3, width: 5, top: 46.5 },
  { id: "later", left: 66.4, width: 11.1, top: 68.4 },
];

// In the Deskwren screenshot: the "Download for Mac" button and the closed
// laptop's lid
const DESKWREN_SPOTS = [
  { id: "download", left: 33.8, width: 16.7, top: 47.1 },
  { id: "lid", left: 33, width: 32.9, top: 76.1 },
];

// Newest first. Copy (kind, desc) lives in t.work.projects[id].
export const PROJECTS = [
  { id: "flikk", name: "Flikk", tag: "App Store", url: "https://mrtkrm.com/flikk", img: flikkImg, spots: FLIKK_SPOTS },
  { id: "deskwren", name: "Deskwren", tag: null, url: "https://mrtkrm.com/deskwren", img: deskwrenImg, spots: DESKWREN_SPOTS },
  { id: "tadoy", name: "Tadoy Café", tag: "Web", url: "https://tadoy-cafe-qr-menu.vercel.app/", img: tadoyImg },
];

// The critter fires this while it is busy inside a screenshot, so the slide
// doesn't change under its feet. detail: { hold: boolean }
export const HOLD_EVENT = "slider:hold";
const HOLD_MAX_MS = 20000; // let go by itself if the "release" never comes

const AUTOPLAY_MS = 6000;
const SWIPE_PX = 50;
const num = (i) => String(i + 1).padStart(2, "0");

const Work = () => {
  const { t } = useLang();
  const w = t.work;
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [held, setHeld] = useState(false);
  const touchX = useRef(null);
  const count = PROJECTS.length;

  const go = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const prev = () => go(index - 1);
  const next = useCallback(() => go(index + 1), [go, index]);

  // Advance on a timer unless the visitor is hovering/focused or prefers less motion
  useEffect(() => {
    let timer = 0;
    const onHold = (e) => {
      clearTimeout(timer);
      setHeld(e.detail.hold);
      if (e.detail.hold) timer = setTimeout(() => setHeld(false), HOLD_MAX_MS);
    };
    window.addEventListener(HOLD_EVENT, onHold);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(HOLD_EVENT, onHold);
    };
  }, []);

  const autoplay = !paused && !held && !reducedMotion;
  useEffect(() => {
    if (!autoplay) return undefined;
    const id = setTimeout(next, AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [autoplay, next]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  };

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > SWIPE_PX) (dx < 0 ? next : prev)();
  };

  return (
    <section id="work" className="section">
      <div className="section__inner">
        <SectionHead fkey="F1" title={w.title} intro={w.intro} />

        <div
          className="slider"
          role="region"
          aria-roledescription="carousel"
          aria-label={w.carousel}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="slider__viewport">
            <ul className="slider__track" style={{ transform: `translateX(-${index * 100}%)` }}>
              {PROJECTS.map((p, i) => {
                const active = i === index;
                const meta = [num(i), w.projects[p.id].kind, p.tag].filter(Boolean).join(" · ");
                return (
                  <li
                    key={p.id}
                    className={`slide${active ? " is-active" : ""}`}
                    data-critter={p.id}
                    aria-roledescription="slide"
                    aria-label={`${num(i)} / ${num(count - 1)} · ${p.name}`}
                    aria-hidden={!active}
                  >
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="slide__shot"
                      tabIndex={active ? 0 : -1}
                    >
                      <span className="slide__frame">
                        <img src={p.img} alt={`${w.shotOf} ${p.name}`} />
                        {(p.spots || []).map((spot) => (
                          <span
                            key={spot.id}
                            className="shot__spot"
                            data-spot={spot.id}
                            style={{ left: `${spot.left}%`, width: `${spot.width}%`, top: `${spot.top}%` }}
                          />
                        ))}
                      </span>
                    </a>
                    <div className="slide__body">
                      <span className="slide__meta">{meta}</span>
                      <h3 className="slide__name">{p.name}</h3>
                      <p className="slide__desc">{w.projects[p.id].desc}</p>
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="slide__visit"
                        tabIndex={active ? 0 : -1}
                      >
                        {w.visit}
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="slider__controls">
            <div className="slider__tabs">
              {PROJECTS.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  className={`slider__tab${i === index ? " is-active" : ""}`}
                  data-critter={p.id}
                  aria-label={p.name}
                  aria-current={i === index}
                  onClick={() => go(i)}
                >
                  <span className="slider__tab-num">{num(i)}</span>
                  <span className="slider__tab-name">{p.name}</span>
                  {i === index && (
                    <span
                      key={`${index}-${autoplay}`}
                      className={`slider__progress${autoplay ? " is-running" : ""}`}
                      style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
                      aria-hidden="true"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="work__more">
          <a href="https://github.com/mertkerimi" className="work__slot" data-critter="github">
            <span className="work__meta">{w.moreMeta}</span>
            <span className="work__slot-title">{w.moreTitle}</span>
          </a>
          <a href="#contact" className="work__slot" data-critter="talk">
            <span className="work__meta">{w.talkMeta}</span>
            <span className="work__slot-title">{w.talkTitle}</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Work;
